import { useTheme } from '@repo/theme';
import { UtilityText } from 'component/text';
import { useTranslation } from 'component/translation';
import { BaseMenu, type BaseMenuProps, MenuItemButton, MenuTrigger, type MenuTriggerProps } from 'component/ui/base-menu';
import { SortButton } from 'component/ui/sort-button';
import type { Key, Selection } from 'react-aria-components';
import type { TLabel } from 'util/stdtypes';
import { t } from 'util/stdtypes';

import styles from './sort-menu.module.css';

export type SortOption = {
    key: string;
    label: TLabel;
};

function SortMenuOption({ option }: { option: SortOption }) {
    const text = useTranslation(option.label);
    return <MenuItemButton id={option.key} text={text} />;
}

export type SortMenuProps = Pick<MenuTriggerProps, 'defaultOpen' | 'isOpen' | 'onOpenChange'> & {
    options: SortOption[];
    value: string;
    onChange: (key: string) => void;
    placement?: BaseMenuProps['placement'];
    /**
     * Autodocs-only — leave undefined in production.
     */
    isNonModal?: BaseMenuProps['isNonModal'];
    testID?: string;
};

function SortMenuHeader() {
    const { colors } = useTheme();
    return (
        <UtilityText caps color={colors.colorTextSecondary} label={t('sort_menu.sort_by_header', 'Sort by')} type='tiny' weight='medium' />
    );
}

export function SortMenu({
    options,
    value,
    onChange,
    placement = 'bottom',
    defaultOpen,
    isOpen,
    isNonModal,
    onOpenChange,
    testID = 'sort-menu',
}: SortMenuProps) {
    const selectedOption = options.find((o) => o.key === value) ?? options[0];
    const selectedText = useTranslation(selectedOption?.label);
    const selectedKeys = selectedOption ? new Set([selectedOption.key]) : new Set<Key>();

    function handleSelectionChange(keys: Selection) {
        if (keys === 'all') return;
        const next = keys.values().next().value;
        if (next === undefined) return;
        onChange(String(next));
    }

    return (
        <MenuTrigger defaultOpen={defaultOpen} isOpen={isOpen} onOpenChange={onOpenChange}>
            <SortButton testID={testID} text={selectedText} />
            <BaseMenu
                header={<SortMenuHeader />}
                isNonModal={isNonModal}
                menuClassName={styles.menu}
                onSelectionChange={handleSelectionChange}
                placement={placement}
                popoverClassName={styles.popover}
                selectedKeys={selectedKeys}
                selectionMode='single'
            >
                {options.map((opt) => (
                    <SortMenuOption key={opt.key} option={opt} />
                ))}
            </BaseMenu>
        </MenuTrigger>
    );
}
